import React, { useCallback, useEffect, useState } from "react";
import { validateArrayIncludes } from "../../../common/validators";
import {
  ORDER_STATUS_DISPATCHABLE,
  ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED,
} from "../../../types/order";
import { useDispatch, useSelector } from "react-redux";
import {
  addOrderToDispatch,
  removeOrderToDispatch,
} from "../../../store/actions/ordersSlice";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import Box from "@mui/material/Box";

const ActionButton = React.memo(({ title, onClick, icon }) => (
  <Stack direction="row">
    <IconButton
      title={title}
      aria-label={`action-button-${title}`}
      onClick={onClick}
      sx={{
        padding: "2px !important",
      }}
    >
      {icon}
    </IconButton>
  </Stack>
));

ActionButton.displayName = "ActionButton";

export const useOrderDispatchToggle = (orderId) => {
  const dispatch = useDispatch();
  const ordersToDispatch = useSelector(
    (state) => state.orders.ordersToDispatch
  );
  const [isChecked, setIsChecked] = useState(
    ordersToDispatch.includes(orderId)
  );

  useEffect(() => {
    setIsChecked(ordersToDispatch.includes(orderId));
  }, [ordersToDispatch, orderId]);

  const handleToggle = useCallback(
    (event) => {
      event.stopPropagation();
      const shouldCheck = !isChecked;

      if (shouldCheck) {
        dispatch(addOrderToDispatch(orderId));
      } else {
        dispatch(removeOrderToDispatch(orderId));
      }
    },
    [dispatch, orderId, isChecked]
  );

  return { isChecked, handleToggle };
};

const OrderActions = ({
  item,
  dashboard,
  acceptOrder,
  enableAcceptOrder = true,
  readyToPickupOrder,
  enableReadyToPickupOrder = true,
  finishOrder,
  enableFinishOrder = true,
  _handleModal,
  enableHandleModal = true,
  cancelDriverRequest,
  enableCancelDriverRequest,
  packed,
  enablePacked,
  extraDelivery,
}) => {
  const { isChecked, handleToggle } = useOrderDispatchToggle(item.order_id);

  if (dashboard) {
    if (item.status_number === 0 && enableAcceptOrder) {
      return (
        <Stack direction="row" spacing={2} alignItems="center">
          <ActionButton
            title="Aceitar"
            onClick={acceptOrder}
            icon={<ThumbUpIcon fontSize="inherit" />}
          />
        </Stack>
      );
    }

    if (item.status_number <= 2 && enablePacked) {
      return (
        <Stack direction="row" spacing={2} alignItems="center">
          <ActionButton
            title="Embalado"
            onClick={packed}
            icon={<BabyChangingStationIcon fontSize="inherit" />}
          />
        </Stack>
      );
    }

    if (item.order_type === "DELIVERY") {
      if (
        validateArrayIncludes(ORDER_STATUS_DISPATCHABLE, item.status_number) &&
        enableHandleModal
      ) {
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <Checkbox
              checked={isChecked}
              onChange={handleToggle}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: "3px" }}
            />
          </Stack>
        );
      } else if (
        !validateArrayIncludes(
          ORDER_STATUS_EXTRA_DELIVERY_DISALLOWED,
          item.status_number
        )
      ) {
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <ActionButton
              title="Corrida Extra"
              onClick={extraDelivery}
              icon={<ControlPointDuplicateIcon fontSize="inherit" />}
            />
          </Stack>
        );
      }
    }
  }

  if (
    item.order_type === "TAKEOUT" &&
    validateArrayIncludes(ORDER_STATUS_DISPATCHABLE, item.status_number) &&
    enableReadyToPickupOrder
  ) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <ActionButton
          title="Pronto para Retirar"
          onClick={readyToPickupOrder}
          icon={<SendIcon fontSize="inherit" />}
        />
      </Stack>
    );
  }

  if (
    item.channel_code === "BALCAO" &&
    validateArrayIncludes([5, 7, 21], item.status_number) &&
    enableFinishOrder
  ) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <ActionButton
          title="Finalizar"
          onClick={finishOrder}
          icon={<SendIcon fontSize="inherit" />}
        />
      </Stack>
    );
  }

  if (enableCancelDriverRequest && item.status_number === 10) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <ActionButton
          title="Cancelar Busca por Motorista"
          onClick={cancelDriverRequest}
          icon={<CancelScheduleSendIcon fontSize="inherit" />}
        />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ width: 24, height: 24 }} />
    </Stack>
  );
};

export default React.memo(OrderActions);
